import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CARDS } from '@lunary/tarot-data'
import type { DailyPickOptionAdminDto, DailyPickSetAdminDto } from '@lunary/api-client'
import { CardTextPreview, sameCardText, THREE_TEXT_FIELDS } from '@lunary/ui-web/card-text'
import type { CardTextValue, ThreeTextKey } from '@lunary/ui-web/card-text'
import { pickFaceFor, useDeck } from '@lunary/ui-web/deck'
import { CardFace } from '../../shared/ui/card-face'
import { api, ApiError } from '../../shared/api/client'
import styles from '../../shared/ui/expert.module.css'
import { dayMonth } from '../../shared/lib/moscow-date'
import { changedFields, usePublishConfirm } from '../../shared/ui/publish-confirm'
import { useThreeCardTexts } from '../decks/queries'
import { ThreeCardPicker } from './card-picker'

const KEY = ['daily-picks']

function message(e: unknown, fallback: string): string {
  return e instanceof ApiError || e instanceof Error ? e.message : fallback
}

export function optionText(option: DailyPickOptionAdminDto): CardTextValue<ThreeTextKey> {
  return { body: option.interpretation, hint: option.hint, advice: option.advice }
}

type Props = { date: string; option: DailyPickOptionAdminDto; readOnly: boolean; published: boolean; canSwap: boolean; taken: readonly string[] }

export function OptionEditor({ date, option, readOnly, published, canSwap, taken }: Props) {
  const qc = useQueryClient()
  const deck = useDeck()
  const texts = useThreeCardTexts()
  const stored = optionText(option)
  const preset = texts.data?.items.find((t) => t.card_id === option.card.id)
  const fallback = preset ? { body: preset.body, hint: preset.hint, advice: preset.advice } : null
  const save = useMutation({
    mutationFn: (text: CardTextValue<ThreeTextKey>) =>
      api.put<DailyPickSetAdminDto>(`/admin/daily-picks/${date}/options/${option.position}/interpretation`, {
        interpretation: text.body,
        hint: text.hint,
        advice: text.advice,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  })
  const [picking, setPicking] = useState(false)
  const swap = useMutation({
    mutationFn: (cardId: string | null) =>
      cardId
        ? api.put<DailyPickSetAdminDto>(`/admin/daily-picks/${date}/options/${option.position}/card`, { card_id: cardId })
        : api.post<DailyPickSetAdminDto>(`/admin/daily-picks/${date}/options/${option.position}/draw`, undefined),
    onSuccess: () => {
      setPicking(false)
      void qc.invalidateQueries({ queryKey: KEY })
    },
  })
  const { ask, confirmed, dialog } = usePublishConfirm()
  const where = `Текст карты ${option.position} в наборе «Трёх карт» на ${dayMonth(date)}`
  const replace = async (cardId: string | null) => {
    if (published) {
      setPicking(false)
      const name = cardId ? CARDS.find((c) => c.id === cardId)?.name : undefined
      const ok = await ask({
        title: 'Заменить опубликованную карту?',
        text: `Карта ${option.position} на ${dayMonth(date)} изменится для всех: вместо «${option.card.name}» будет ${name ? `«${name}»` : 'случайная карта'}, текст заменится описанием по умолчанию. Кто уже открыл эту карту, сразу увидит новую.`,
        confirmLabel: 'Заменить',
        facts: [
          ['Уже открыли', String(option.opened_count)],
          ['Зафиксировали своей', String(option.chosen_count)],
        ],
      })
      if (!ok) return
    }
    swap.mutate(cardId)
  }
  const onSave = async (next: CardTextValue<ThreeTextKey>) => {
    if (published)
      await confirmed({
        title: 'Опубликовать изменения?',
        text: `${where} изменится для всех пользователей.`,
        confirmLabel: 'Опубликовать',
        facts: [['Меняется', changedFields(THREE_TEXT_FIELDS, stored, next)]],
      })
    return save.mutateAsync(next)
  }
  const onReset = async (text: CardTextValue<ThreeTextKey>) => {
    if (
      published &&
      !(await ask({
        title: 'Вернуть текст по умолчанию?',
        text: `${where} заменится описанием по умолчанию для «${option.card.name}». Это изменится для всех пользователей.`,
        confirmLabel: 'Вернуть и опубликовать',
      }))
    )
      return
    save.mutate(text)
  }
  const swapError = swap.error ? message(swap.error, 'Не удалось сменить карту') : null
  const error = save.error ? message(save.error, 'Что-то пошло не так') : swapError && !picking ? swapError : null

  return (
    <div className={styles.optionPreview}>
      <CardTextPreview
        fields={THREE_TEXT_FIELDS}
        value={stored}
        face={<CardFace src={pickFaceFor(deck, option.card)} number={option.card.arcana === 'major' ? option.card.number : null} name={option.card.name} size="lg" />}
        eyebrow={`Карта ${option.position} · Людмила о карте`}
        name={option.card.name}
        status={fallback ? (sameCardText(THREE_TEXT_FIELDS, stored, fallback) ? 'default' : 'changed') : null}
        saving={save.isPending}
        saved={save.isSuccess}
        error={error}
        readOnly={readOnly}
        saveLabel={published ? 'Опубликовать…' : 'Сохранить'}
        savedLabel={published ? 'Опубликовано' : 'Сохранено'}
        onSave={onSave}
        onReset={fallback ? () => void onReset(fallback) : undefined}
        resetting={save.isPending}
        footer={(editing) => (
          <>
            {canSwap && !readOnly && (
              <div className={styles.actions}>
                <button type="button" className="secondary" disabled={editing || swap.isPending} onClick={() => setPicking(true)}>
                  Сменить карту
                </button>
                <button type="button" className="secondary" disabled={editing || swap.isPending} title="Другая случайная карта, без повторов в наборе" onClick={() => void replace(null)}>
                  {swap.isPending && !picking ? 'Тяну…' : 'Случайная'}
                </button>
              </div>
            )}
            <p className={styles.meaning}>{option.card.upright_meaning}</p>
          </>
        )}
      />
      {picking && (
        <ThreeCardPicker
          open
          position={option.position}
          current={option.card.id}
          taken={taken}
          pending={swap.isPending}
          error={swapError}
          onClose={() => {
            setPicking(false)
            swap.reset()
          }}
          onPick={(cardId) => void replace(cardId)}
        />
      )}
      {dialog}
    </div>
  )
}
